import { useState, useEffect, useMemo } from 'react';
import { TimePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { DayOfWeek } from '@/proto/logiblocks/logiblocks';
import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import styles from '../NodeCustomization.module.css';
import type { CustomizationComponent } from '@/pages/Logiblocks/types';
import { DayOfWeekPicker } from '../DayOfWeekPicker';
import { FieldErrorLabel, FieldLabel } from '../shared';
import { ScheduleTriggerFields } from './ScheduleTriggerNodeInfo';
import { getUserTimeZone } from '@/common/utils/utils';
import {
  formatTimezoneOffset,
  utcHourMinuteToUser,
  userHourMinuteToUtc,
  shiftDayOfWeek,
} from '@/pages/Logiblocks/dateTimeUtils';

type ScheduleMode = 'daily' | 'days_of_week';

interface ScheduleFormState {
  mode: ScheduleMode;
  hour: number;
  minute: number;
  daysOfWeek: DayOfWeek[];
}

/**
 * Extracts local form state from the protobuf node data.
 * Converts UTC hour/minute to the user's timezone for display.
 * For days_of_week mode, also shifts days to account for midnight crossings.
 */
function deriveState(nodeData: LBNodeData, tzName: string): ScheduleFormState {
  const schedule = nodeData.schedule_trigger;
  if (schedule?.days_of_week) {
    const { hour, minute, dayOffset } = utcHourMinuteToUser(
      schedule.days_of_week.hour,
      schedule.days_of_week.minute,
      tzName
    );
    return {
      mode: 'days_of_week',
      hour,
      minute,
      daysOfWeek: schedule.days_of_week.days_of_week.map((d) =>
        shiftDayOfWeek(d, dayOffset)
      ),
    };
  }
  const utcHour = schedule?.daily?.hour ?? 9;
  const utcMinute = schedule?.daily?.minute ?? 0;
  const { hour, minute } = utcHourMinuteToUser(utcHour, utcMinute, tzName);
  return {
    mode: 'daily',
    hour,
    minute,
    daysOfWeek: [DayOfWeek.MONDAY],
  };
}

/**
 * Builds protobuf node data from form state.
 * Converts user-local hour/minute back to UTC for storage.
 * For days_of_week mode, also shifts days to account for midnight crossings.
 */
function buildNodeData(
  mode: ScheduleMode,
  hour: number,
  minute: number,
  daysOfWeek: DayOfWeek[],
  tzName: string
): Partial<LBNodeData> {
  const utc = userHourMinuteToUtc(hour, minute, tzName);
  if (mode === 'days_of_week') {
    return {
      schedule_trigger: {
        days_of_week: {
          days_of_week: daysOfWeek.map((d) => shiftDayOfWeek(d, utc.dayOffset)),
          hour: utc.hour,
          minute: utc.minute,
        },
      },
    };
  }
  return {
    schedule_trigger: { daily: { hour: utc.hour, minute: utc.minute } },
  };
}

export const ScheduleTriggerCustomization: CustomizationComponent = ({
  nodeData,
  onChange,
  isReadOnly = false,
  validationErrors,
}) => {
  const scheduleTypeError = validationErrors?.find(
    (e) => e.field === ScheduleTriggerFields.SCHEDULE_TYPE
  );
  const timezoneInfo = useMemo(() => {
    const tz = getUserTimeZone();
    return {
      name: tz.name,
      displayString: `Timezone set to (${formatTimezoneOffset(tz.offsetMinutes)}) ${tz.name}`,
    };
  }, []);

  const [mode, setMode] = useState<ScheduleMode>(
    () => deriveState(nodeData, timezoneInfo.name).mode
  );
  const [hour, setHour] = useState(
    () => deriveState(nodeData, timezoneInfo.name).hour
  );
  const [minute, setMinute] = useState(
    () => deriveState(nodeData, timezoneInfo.name).minute
  );
  const [daysOfWeek, setDaysOfWeek] = useState(
    () => deriveState(nodeData, timezoneInfo.name).daysOfWeek
  );

  // Sync local state when nodeData changes externally (e.g. switching between nodes)
  useEffect(() => {
    const state = deriveState(nodeData, timezoneInfo.name);
    setMode(state.mode);
    setHour(state.hour);
    setMinute(state.minute);
    setDaysOfWeek(state.daysOfWeek);
  }, [nodeData, timezoneInfo.name]);

  const handleModeChange = (newMode: ScheduleMode) => {
    setMode(newMode);
    onChange(
      buildNodeData(newMode, hour, minute, daysOfWeek, timezoneInfo.name)
    );
  };

  const handleDaysChange = (newDays: DayOfWeek[]) => {
    setDaysOfWeek(newDays);
    onChange(buildNodeData(mode, hour, minute, newDays, timezoneInfo.name));
  };

  const handleTimeChange = (timeValue: Dayjs | null) => {
    const newHour = timeValue ? timeValue.hour() : 0;
    const newMinute = timeValue ? timeValue.minute() : 0;
    setHour(newHour);
    setMinute(newMinute);
    onChange(
      buildNodeData(mode, newHour, newMinute, daysOfWeek, timezoneInfo.name)
    );
  };

  return (
    <>
      <div className={styles.customizationSection}>
        <FieldLabel required className={styles.sectionLabel}>
          Schedule Type
        </FieldLabel>
        <select
          value={mode}
          onChange={(e) => handleModeChange(e.target.value as ScheduleMode)}
          className={styles.selectField}
          disabled={isReadOnly}
        >
          <option value='daily'>Daily</option>
          <option value='days_of_week'>Days of Week</option>
        </select>
        <FieldErrorLabel error={scheduleTypeError} />
      </div>

      {mode === 'days_of_week' && (
        <div className={styles.customizationSection}>
          <label className={styles.sectionLabel}>Days</label>
          <DayOfWeekPicker
            selectedDays={daysOfWeek}
            onChange={handleDaysChange}
            disabled={isReadOnly}
          />
        </div>
      )}

      <div className={styles.customizationSection}>
        <label className={styles.sectionLabel}>Time</label>
        <TimePicker
          value={dayjs().hour(hour).minute(minute)}
          onChange={handleTimeChange}
          format='h:mm A'
          use12Hours
          disabled={isReadOnly}
          style={{
            width: '100%',
            borderRadius: '4px',
          }}
        />
        <span
          style={{
            fontSize: '12px',
            color: 'var(--text-secondary)',
            marginTop: '4px',
            display: 'block',
          }}
        >
          {timezoneInfo.displayString}
        </span>
      </div>
    </>
  );
};
